import { Link, useForm } from "@inertiajs/react"

function getYouTubeThumbnailUrl(url: string): string | null {
  const match = url.match(
    /(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([^&?/\s]+)/,
  )
  return match ? `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg` : null
}
import {
  ChevronDown,
  Film,
  Heart,
  MessageCircle,
  Play,
  Send,
} from "lucide-react"
import { useState } from "react"

import ContentActionMenu from "@/components/content-action-menu"
import MentionText from "@/components/feed/mention-text"
import VideoPlayer from "@/components/players/video-player"
import ShareButton from "@/components/share-button"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import VerifiedBadge from "@/components/verified-badge"
import { typedDelete, typedPost } from "@/lib/typed-router"
import type { LikePost, UnlikePost } from "@/types/router-mutations"
import type { Post, PostFeedTrait } from "@/types/serializers"
import type RequestsCreateComment from "@/types/serializers/RequestsCreateComment"

export type PostData = Post & PostFeedTrait

interface PostCardProps {
  post: PostData
  showViewAllComments?: boolean
}

const REACTIONS = [
  { type: "heart", emoji: "❤️", label: "Me gusta" },
  { type: "fire", emoji: "🔥", label: "Fuego" },
  { type: "clap", emoji: "👏", label: "Aplauso" },
  { type: "goal", emoji: "⚽", label: "Golazo" },
  { type: "trophy", emoji: "🏆", label: "Crack" },
] as const

function timeAgo(dateString: string): string {
  const now = new Date()
  const date = new Date(dateString)
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000)

  if (seconds < 60) return "hace un momento"
  if (seconds < 3600) return `hace ${Math.floor(seconds / 60)} min`
  if (seconds < 86400) return `hace ${Math.floor(seconds / 3600)}h`
  if (seconds < 604800) return `hace ${Math.floor(seconds / 86400)}d`
  return date.toLocaleDateString("es-AR")
}

const roleLabels: Record<string, string> = {
  player: "JUGADOR",
  scout: "SCOUT",
  club: "CLUB",
  admin: "ADMIN",
}

function profilePath(user: PostData["user"]): string {
  const slug = user.profile_slug
  if (!slug) return "#"
  if (user.role === "scout") return `/scouts/${slug}`
  if (user.role === "club") return `/clubes/${slug}`
  return `/jugadores/${slug}`
}

export default function PostCard({
  post,
  showViewAllComments = false,
}: PostCardProps) {
  const [showComments, setShowComments] = useState(false)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [playingHighlight, setPlayingHighlight] = useState(false)
  const commentForm = useForm<RequestsCreateComment>({ comment: { body: "" } })

  const userReactions = post.user_reactions ?? []
  const reactionCounts = post.reaction_counts ?? {}
  const hasReacted = userReactions.length > 0

  const handleReaction = (reactionType: string) => {
    if (userReactions.includes(reactionType)) {
      typedDelete<UnlikePost>(
        `/publicaciones/${post.id}/me-gusta`,
        { reaction_type: reactionType },
        { preserveScroll: true },
      )
    } else {
      typedPost<LikePost>(
        `/publicaciones/${post.id}/me-gusta`,
        { reaction_type: reactionType },
        { preserveScroll: true },
      )
    }
    setPickerOpen(false)
  }

  const handleQuickReaction = (e: React.MouseEvent) => {
    e.preventDefault()
    handleReaction("heart")
  }

  const handleCommentSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    commentForm.post(`/publicaciones/${post.id}/comentarios`, {
      preserveScroll: true,
      onSuccess: () => commentForm.reset(),
    })
  }

  return (
    <div className="border-border border-b px-4 py-4">
      {/* Author header */}
      <div className="mb-3 flex items-center gap-3">
        <Link href={profilePath(post.user)}>
          <Avatar className="h-[55px] w-[55px] shrink-0">
            <AvatarImage
              src={post.user.avatar_url ?? undefined}
              alt={post.user.full_name}
              className="object-cover"
            />
            <AvatarFallback className="bg-krown-200 text-sm font-bold text-white">
              {post.user.first_name[0]}
              {post.user.last_name[0]}
            </AvatarFallback>
          </Avatar>
        </Link>
        <div className="flex-1">
          <div className="flex items-center gap-1.5">
            <Link
              href={profilePath(post.user)}
              className="text-sm font-bold text-white hover:underline"
            >
              {post.user.full_name}
            </Link>
            <VerifiedBadge
              show={post.user.profile_verified ?? false}
              size="sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground text-[11px] tracking-wider uppercase">
              {post.user.role
                ? (roleLabels[post.user.role] ?? post.user.role)
                : ""}
            </span>
            <span className="text-krown-grey-50 text-[11px]">·</span>
            <span className="text-krown-grey-50 text-[11px]">
              {timeAgo(post.created_at)}
            </span>
          </div>
        </div>
        <ContentActionMenu
          reportableType="Post"
          reportableId={post.id}
          showBlock
          blockUserId={post.user.id}
        />
      </div>

      {/* Highlight content */}
      {post.feedable_type === "Highlight" && post.feedable_data && (
        <div className="mb-3">
          <div className="bg-krown-accent/10 border-krown-accent/30 mb-2 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5">
            <Film className="text-krown-accent h-3 w-3" />
            <span className="text-krown-accent text-[11px] font-semibold tracking-wider uppercase">
              Highlight
            </span>
          </div>
          {playingHighlight ? (
            <VideoPlayer
              streamEmbedUrl={post.feedable_data.stream_embed_url}
              videoUrl={post.feedable_data.video_url}
              streamStatus={
                post.feedable_data.stream_status as
                  | "pending"
                  | "processing"
                  | "ready"
                  | "error"
              }
            />
          ) : (
            <button
              className="group relative w-full"
              onClick={() => setPlayingHighlight(true)}
              aria-label={`Reproducir: ${post.feedable_data.title}`}
            >
              <div className="relative aspect-video overflow-hidden rounded-lg">
                {post.feedable_data.thumbnail_url ? (
                  <img
                    src={post.feedable_data.thumbnail_url}
                    alt={post.feedable_data.title}
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                ) : getYouTubeThumbnailUrl(post.feedable_data.video_url) ? (
                  <img
                    src={getYouTubeThumbnailUrl(post.feedable_data.video_url)!}
                    alt={post.feedable_data.title}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <video
                    src={post.feedable_data.video_url}
                    preload="metadata"
                    className="h-full w-full object-cover"
                    muted
                    playsInline
                  />
                )}
                <div className="absolute inset-0 flex items-center justify-center bg-black/20 transition-colors group-hover:bg-black/40">
                  <div className="bg-background/70 flex h-14 w-14 items-center justify-center rounded-full">
                    <Play className="h-7 w-7 fill-white text-white" />
                  </div>
                </div>
              </div>
            </button>
          )}
          <div className="mt-2 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-white">
              {post.feedable_data.title}
            </h3>
            <Link
              href={`/jugadores/${post.feedable_data.player_slug}`}
              className="text-krown-100 text-xs font-medium transition-colors hover:text-white"
            >
              Ver perfil
            </Link>
          </div>
          {post.feedable_data.description && (
            <p className="text-muted-foreground mt-0.5 text-xs">
              {post.feedable_data.description}
            </p>
          )}
        </div>
      )}

      {/* Body (hide for highlight posts since title is shown above) */}
      {post.body && !post.feedable_type && (
        <p className="text-krown-grey-30 mb-3 text-sm">
          <MentionText text={post.body} />
        </p>
      )}

      {/* Media */}
      {post.post_media.length > 0 && (
        <div className="mb-3 overflow-hidden rounded-lg">
          {post.post_media.map((media) =>
            media.media_type === "image" ? (
              <img
                key={media.id}
                src={media.media_url}
                alt="media"
                loading="lazy"
                className="max-h-96 w-full object-cover"
              />
            ) : (
              <video
                key={media.id}
                src={media.media_url}
                controls
                playsInline
                className="max-h-96 w-full"
              />
            ),
          )}
        </div>
      )}

      {/* Reaction counts summary */}
      {Object.keys(reactionCounts).length > 0 && (
        <div className="text-muted-foreground flex items-center gap-2 pb-2 text-xs">
          {REACTIONS.filter((r) => reactionCounts[r.type]).map((r) => (
            <span key={r.type} className="flex items-center gap-0.5">
              <span>{r.emoji}</span>
              <span>{reactionCounts[r.type]}</span>
            </span>
          ))}
          <span className="ml-auto">{post.likes_count}</span>
        </div>
      )}

      {/* Action bar */}
      <div className="flex items-center gap-5 pt-2">
        <div className="flex items-center gap-1">
          <button
            onClick={handleQuickReaction}
            aria-label="Reaccionar"
            className={`flex items-center gap-1.5 text-sm transition-all active:scale-125 ${hasReacted ? "text-krown-crown" : "text-muted-foreground hover:text-krown-crown"}`}
          >
            <Heart className={`h-5 w-5 ${hasReacted ? "fill-current" : ""}`} />
            {post.likes_count > 0 && <span>{post.likes_count}</span>}
          </button>
          <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
            <PopoverTrigger asChild>
              <button
                aria-label="Más reacciones"
                className="text-muted-foreground hover:text-krown-crown p-0.5 transition-colors"
              >
                <ChevronDown className="h-3 w-3" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-2" side="top" align="start">
              <div className="flex gap-1">
                {REACTIONS.map((reaction) => (
                  <button
                    key={reaction.type}
                    onClick={() => handleReaction(reaction.type)}
                    aria-label={reaction.label}
                    className={`hover:bg-muted rounded-full p-2 text-lg transition-all hover:scale-125 ${
                      userReactions.includes(reaction.type)
                        ? "bg-muted ring-krown-crown ring-1"
                        : ""
                    }`}
                  >
                    {reaction.emoji}
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </div>
        <button
          onClick={() => setShowComments(!showComments)}
          className="text-muted-foreground hover:text-krown-200 flex items-center gap-1.5 text-sm transition-colors"
        >
          <MessageCircle className="h-5 w-5" />
          <span>{post.comments_count}</span>
        </button>
        {showViewAllComments && showComments && (
          <Link
            href={`/publicaciones/${post.id}/comentarios`}
            className="text-muted-foreground hover:text-krown-200 text-xs transition-colors hover:underline"
          >
            Ver todos
          </Link>
        )}
        <ShareButton
          url={`${window.location.origin}/feed`}
          title={`${post.user.full_name} en KrownFT`}
          variant="icon"
        />
      </div>

      {/* Inline comments section */}
      {showComments && (
        <div className="border-border mt-3 border-t pt-3">
          <form onSubmit={handleCommentSubmit} className="flex gap-2">
            <Input
              placeholder="Escribí un comentario..."
              value={commentForm.data.comment.body}
              onChange={(e) =>
                commentForm.setData("comment", {
                  ...commentForm.data.comment,
                  body: e.target.value,
                })
              }
              className="flex-1 text-sm"
            />
            <Button
              type="submit"
              size="sm"
              disabled={
                commentForm.processing || !commentForm.data.comment.body.trim()
              }
              className="bg-krown-200 hover:bg-krown-100 text-white"
            >
              <Send className="mr-1 h-3 w-3" />
              Enviar
            </Button>
          </form>
        </div>
      )}
    </div>
  )
}
